import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  Grid,
  Button,
  IconButton,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import MDButton from "../../../../components/MDButton";
import Icon from "@mui/material/Icon";
import React, { useEffect, useState } from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";
import { post, get } from "requests";
import AffiliatedBusinessForm from "./AffiliatedBusinessForm";
import { useMerchantContext } from "./MerchantContext";

const AffiliatedBusiness = () => {
  const {
    affiliatedBusinesses,
    setAffiliatedBusinessesData,
    merchantBusinessInfo,
    merchantAndOwners,
    fetchMerchantData,
  } = useSubmissionStore();
  const { clearData } = useMerchantContext();
  const [isExpanded, setIsExpanded] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<any | null>(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [initialData, setInitialData] = useState<any | null>(null);

  useEffect(() => {
    if (clearData) {
      console.log("cleared data");
      setAffiliatedBusinessesData([]);
    }
  }, [clearData]);

  const handleAddAffiliation = () => {
    if (merchantAndOwners?.users?.length > 0) {
      setInitialData(null);
      setOpenModal(true);
    } else {
      setOpenAlert(true);
    }
  };

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleOpenModal = (business: any) => {
    setSelectedBusiness(business);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedBusiness(null);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const deleteAffiliatedBusiness = async (businessId: string) => {
    try {
      const response = await post("/deleteAffiliatedBusiness", { id: businessId });
      if (merchantBusinessInfo?.merchantId) {
        fetchMerchantData(merchantBusinessInfo?.merchantId);
      }
    } catch (error) {
      console.error("Error deleting business:", error);
    }
  };

  const getBusinessDetails = () => {
    return affiliatedBusinesses.map((business: any, index: number) => {
      const businessName = business?.businessLegalName || "";
      const address = business?.abaPhysicalAddress || "";
      const fullAddress = `${address?.address || ""}, ${address?.city || ""}, ${
        address?.state || ""
      }, ${address?.zipCode || ""}`;
      const businessId = business?.id;
      const role = business?.isSeller ? "Seller" : "Guarantor";
      const roleColor = business?.isSeller ? "#3e848d" : "#086e3e";
      const roleBackground = business?.isSeller ? "#dce4e5" : "#abcbbc";

      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 0",
            paddingTop: "2px",
            paddingBottom: "2px",
            paddingLeft: "20px",
            paddingRight: "30px",
            borderRadius: "11px",
            "&:hover": {
              backgroundColor: "#f9f9f9",
              boxShadow: "none",
            },
            marginBottom: "0px",
            marginLeft: "1px",
            marginTop: "1px",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <Typography sx={{ fontSize: "15px", fontWeight: "bold" }}>{businessName}</Typography>

              <Button
                sx={{
                  backgroundColor: roleBackground,
                  color: roleColor,
                  textTransform: "uppercase",
                  fontSize: "12px",
                  fontWeight: "bold",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                  paddingTop: "2px",
                  paddingBottom: "2px",
                  marginTop: "5px",
                  borderRadius: "11px",
                  "&:hover": {
                    backgroundColor: roleBackground,
                    boxShadow: "none",
                  },
                  width: "auto",
                  minWidth: "50px",
                  minHeight: "28px",
                }}
              >
                {role}
              </Button>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "text.secondary",
                  marginTop: "3px",
                  marginBottom: "10px",
                }}
              >
                {fullAddress}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                marginRight: "15px",
              }}
              onClick={() => deleteAffiliatedBusiness(businessId)}
            >
              <Icon sx={{ color: "error.main", fontSize: "13px" }}>delete</Icon>
              <Typography
                sx={{
                  color: "error.main",
                  fontSize: "13px",
                  fontWeight: "500",
                  marginLeft: "5px",
                }}
              >
                DELETE
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                marginRight: "14px",
              }}
              onClick={() => handleOpenModal(business)}
            >
              <Icon sx={{ color: "primary.main", fontSize: "13px" }}>edit</Icon>
              <Typography
                sx={{
                  color: "primary.main",
                  fontSize: "13px",
                  fontWeight: "500",
                  marginLeft: "5px",
                }}
              >
                EDIT
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
        marginBottom: "12px",
        border: "1px solid #f0f0f0",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "16px" }}>
          Affiliated Businesses
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", marginRight: "10px" }}>
          {isExpanded && (
            <MDButton variant="gradient" color="primary" onClick={handleAddAffiliation}>
              + Add Affiliation
            </MDButton>
          )}
          <IconButton onClick={toggleExpand} sx={{ marginLeft: "8px" }}>
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {isExpanded && <>{getBusinessDetails()}</>}

      <AffiliatedBusinessForm
        open={openModal}
        onClose={handleCloseModal}
        onSave={(data) => console.log("Saved data:", data)}
        initialData={selectedBusiness}
      />

      {/* Alert Dialog */}
      <Dialog open={openAlert} onClose={handleCloseAlert}>
        <DialogTitle>{"Error"}</DialogTitle>
        <DialogContent>
          <DialogContentText>At least one owner is mandatory.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAlert} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AffiliatedBusiness;
